import React from 'react';
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  CategoryScale,
  Title,
  Tooltip,
  Filler,
  BarElement,
  Legend,
  ArcElement
} from 'chart.js';
import {Line, Bar, Doughnut} from 'react-chartjs-2';
import { Box, Typography, useTheme } from '@mui/material';
import 'chartjs-adapter-date-fns';
import { isMobile } from 'react-device-detect';

ChartJS.register(
  TimeScale,
  LinearScale,
  CategoryScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Filler,
  Legend,
  ArcElement
);

const labels = ['January', 'February', 'March', 'April', 'May', 'June', 'July'];

export function alphaColor(color, alpha) {

  var R = parseInt(color.substring(1,3),16);
  var G = parseInt(color.substring(3,5),16);
  var B = parseInt(color.substring(5,7),16);

  return "rgba(" + R + "," + G + "," + B + "," + alpha + ")"
}

interface DataChartOptions {
  data: any;
}

export function DataChart(props: DataChartOptions) {
    return <Line data={props.data} width={"100%"} options={{
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
              position: 'bottom',
              display: false,
              labels: {
                usePointStyle: true,
                pointStyle: 'rectRounded'
              }
            },
        },
    }} />
}

export const getBarChartOptions = (front = false, unit = undefined) => ({
    responsive: true,
    maintainAspectRatio: false,
    borderRadius: 0,
    interaction: {
        mode: 'index',
        intersect: false,
    },
    plugins: {
        legend: {
          position: 'bottom',
          display: true,
          labels: {
            usePointStyle: true,
            pointStyle: 'rectRounded'
          }
        },
        tooltip: {
            callbacks: {
                label: (t) => t.dataset.label + ": " + (front ? (unit ? unit : "") : "") + Math.round(100000*t.raw)/100000 + (!front ? (unit ? unit : "") : "")
            }
        }
    },
    scales: {
        x: {
            type: 'category',
            stacked: true,
            grid: {
                offset: false,
            },
            offset: true
        },
        y: {
            stacked: true,
            afterFit(scale) {
                scale.width = 100;
              },
            ticks: {
                callback: function(value, index, ticks) {
                    return (front ? (unit ? unit : "") : "") + (Math.round(value * 100000)/100000).toString() + (!front ? (unit ? unit : "") : "")
                }
            },
            max: null
        },
    },
})

export const getBarTimeChartOptions = (front = false, unit = undefined) => ({
    responsive: true,
    maintainAspectRatio: false,
    borderRadius: 5,
    interaction: {
        mode: 'x',
        intersect: false,
    },
    plugins: {
        legend: {
          position: 'bottom',
          display: true,
          labels: {
            usePointStyle: true,
            pointStyle: 'rectRounded'
          }
        },
        tooltip: {
            callbacks: {
                label: (t) => t.dataset.label + ": " + (front ? (unit ? unit : "") : "") + Math.round(100000*t.raw)/100000 + (!front ? (unit ? unit : "") : "")
            }
        }
    },
    scales: {
        x: {
            type: 'time',
            time: {
                tooltipFormat:'dd/MM/yyyy HH:mm',
                displayFormats: {
                    hour: 'HH:mm'
                },
                unit: "hour",
                stepSize: 1,
            },
            stacked: true,
            grid: {
                offset: false,
            },
            offset: true
        },
        y: {
            stacked: true,
            afterFit(scale) {
                scale.width = 100; // fixed width
              },
            ticks: {
                // Include a dollar sign in the ticks
                callback: function(value, index, ticks) {
                    return (front ? (unit ? unit : "") : "") + (Math.round(value * 100000)/100000).toString() + (!front ? (unit ? unit : "") : "")
                }
            },
            max: null
        },
        
    },
})

interface BarChartOptions {
  data: any;
  options: any;
}

export function BarChart(props: BarChartOptions) {
    return <Bar data={props.data} width={"calc(100% + 50)"} style={{marginLeft: -50}} options={props.options ? props.options : getBarChartOptions()} />
}

interface BarTimeChartOptions {
  data: any;
  options: any;
}

export function BarTimeChart(props: BarTimeChartOptions) {
    return <Bar data={props.data} width={"calc(100% + 50)"} style={{marginLeft: -50}} options={props.options ? props.options : getBarTimeChartOptions()} />
}

export const getDoughnutChartOptions = (front = false, unit = undefined) => ({
  responsive: true,
  maintainAspectRatio: false,
  cutout: 70,
  borderRadius: 5,
  plugins: {
    legend: {
      position: isMobile ? 'bottom': 'chartArea',
      display: true,
      labels: {
        usePointStyle: true,
        pointStyle: 'rectRounded'
      }
    },
    tooltip: {
      callbacks: {
        label: (t) => t.dataset.label + ": " + (front ? (unit ? unit : "") : "") + Math.round(100000*t.raw)/100000 + (!front ? (unit ? unit : "") : "")
      }
    }
  }
})

interface DoughnutChartOptions {
  data: any;
  options: any;
}

export function DoughnutChart(props: DoughnutChartOptions) {
  return <Doughnut data={props.data} width={"calc(100% + 50)"} options={props.options ? props.options : getDoughnutChartOptions()} />
}

export const getTimeChartOptions = (unit = undefined, front = false, ) => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
              position: 'bottom',
              display: true,
              labels: {
                usePointStyle: true,
                pointStyle: 'rectRounded'
              }
            },
            tooltip: {
                callbacks: {
                    label: (t) => t.dataset.label + ": " + (front ? (unit ? unit : "") : "") + Math.round(100000*t.raw)/100000 + (!front ? (unit ? unit : "") : "")
                }
            },
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    tooltipFormat:'dd/MM/yyyy HH:mm',
                    displayFormats: {
                        hour: 'HH:mm',
                        minute: 'HH:mm'
                    },
                    unit: "hour",
                    unitStepSize: 1,
                },
                offset: true
            },
            y: {
                type: 'linear',
                display: true,
                afterFit(scale) {
                    scale.width = 100; // fixed width
                  },
                ticks: {
                    // Include a dollar sign in the ticks
                    callback: function(value, index, ticks) {
                        return (front ? (unit ? unit : "") : "") + (Math.round(value * 100000)/100000).toString() + (!front ? (unit ? unit : "") : "")
                    }
                },
            }
        }
    })

interface TimeChartOptions {
  data: any;
  options: any;
}

export function TimeChart(props: TimeChartOptions) {
    return <Line data={props.data} width={"calc(100% + 50)"} style={{marginLeft: -50}} options={props.options ? props.options : getTimeChartOptions()} />
}